/* ==========================================================================
   Nuvora theme — global scripts
   Small, dependency-free custom elements.
   ========================================================================== */

(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const root = document.documentElement;
  window.themeReady = true;

  if (window.Shopify && window.Shopify.designMode) root.classList.add('shopify-design-mode');

  /* ---------- Helpers ---------- */
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const showToast = (message) => {
    const toast = document.getElementById('ThemeToast');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(showToast.timer);
    showToast.timer = setTimeout(() => {
      toast.hidden = true;
    }, 3200);
  };

  /* ---------- Reveal on scroll ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  const initReveal = (scope = document) => {
    const items = scope.querySelectorAll('.reveal:not(.is-visible)');
    if (!revealObserver || root.classList.contains('no-animations')) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Cart helpers ---------- */
  const getCartSections = () => {
    const ids = ['cart-icon-bubble'];
    if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
    document.querySelectorAll('cart-items[data-section]').forEach((el) => {
      const id = el.dataset.section;
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const next = doc.querySelector('[data-cart-drawer-panel]');
        const current = document.querySelector('#CartDrawer [data-cart-drawer-panel]');
        if (next && current) current.innerHTML = next.innerHTML;
      } else if (id === 'cart-icon-bubble') {
        const next = doc.querySelector('.shopify-section') || doc.body;
        const current = document.getElementById('cart-icon-bubble');
        if (current) current.innerHTML = next.innerHTML;
      } else {
        const next = doc.getElementById(`MainCart-${id}`);
        const current = document.getElementById(`MainCart-${id}`);
        if (next && current) current.innerHTML = next.innerHTML;
      }
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
  };

  const addToCart = async (formData) => {
    const sections = getCartSections();
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);
    const response = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || theme.strings.cartError);
    }
    return data;
  };

  const afterAddToCart = (data) => {
    const drawer = document.getElementById('CartDrawer');
    if (theme.cartType === 'drawer' && drawer) {
      renderCartSections(data.sections);
      drawer.open();
    } else {
      window.location.href = theme.routes.cart;
    }
  };

  /* ==========================================================================
     Announcement bar
     ========================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.announcement-bar__slide')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.delay = Number(this.dataset.autoplay || 5) * 1000;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.play());
      this.play();
    }

    disconnectedCallback() {
      this.stop();
    }

    go(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.index);
        slide.setAttribute('aria-hidden', String(i !== this.index));
      });
    }

    play() {
      this.stop();
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ==========================================================================
     Sticky header
     ========================================================================== */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.isSticky = this.classList.contains('header-wrapper--sticky');
      const section = this.closest('.shopify-section');
      if (this.isSticky && section) section.classList.add('is-sticky');
      this.onScroll = this.onScroll.bind(this);
      this.setHeight = this.setHeight.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.setHeight);
      this.setHeight();
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.setHeight);
    }

    setHeight() {
      root.style.setProperty('--header-height', this.isSticky ? `${this.offsetHeight}px` : '0px');
    }

    onScroll() {
      if (!this.isSticky) return;
      const scrolled = window.scrollY > 40;
      if (scrolled !== this.scrolled) {
        this.scrolled = scrolled;
        this.classList.toggle('is-scrolled', scrolled);
        setTimeout(this.setHeight, 320);
      }
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ==========================================================================
     Desktop menu (hover + click + keyboard)
     ========================================================================== */
  class HeaderMenu extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll(':scope > ul > li > details')];
      const canHover = window.matchMedia('(hover: hover)').matches;

      this.items.forEach((details) => {
        const item = details.parentElement;
        const summary = details.querySelector('summary');
        if (canHover) {
          item.addEventListener('mouseenter', () => {
            clearTimeout(details.closeTimer);
            this.closeAll(details);
            details.open = true;
          });
          item.addEventListener('mouseleave', () => {
            details.closeTimer = setTimeout(() => {
              details.open = false;
            }, 120);
          });
          summary.addEventListener('click', (event) => {
            if (event.detail > 0) {
              event.preventDefault();
              details.open = true;
            }
          });
        }
        details.addEventListener('toggle', () => {
          if (details.open) this.closeAll(details);
        });
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this.onKeyUp = (event) => {
        if (event.key !== 'Escape') return;
        const open = this.items.find((d) => d.open);
        if (open) {
          open.open = false;
          open.querySelector('summary').focus();
        }
      };
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keyup', this.onKeyUp);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keyup', this.onKeyUp);
    }

    closeAll(except) {
      this.items.forEach((d) => {
        if (d !== except) d.open = false;
      });
    }
  }
  customElements.define('header-menu', HeaderMenu);

  /* ==========================================================================
     Drawers (menu, filters, cart)
     ========================================================================== */
  class DrawerComponent extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (this.classList.contains('is-open')) trapFocus(this.panel, event);
      });
    }

    get panel() {
      return this.querySelector('.drawer__panel');
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        const focusTarget = this.panel.querySelector('[data-autofocus]') || this.panel;
        focusTarget.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (typeof this.trigger.focus === 'function') this.trigger.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('drawer-component', DrawerComponent);

  class CartDrawer extends DrawerComponent {}
  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(opener);
      }
      return;
    }

    const cartToggle = event.target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer') {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) {
        event.preventDefault();
        drawer.open(cartToggle);
      }
    }
  });

  /* ==========================================================================
     Search modal + predictive search
     ========================================================================== */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.defaultContent = this.results ? this.results.innerHTML : '';
      this.predictive = this.dataset.predictive === 'true';

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-search-open]');
        if (trigger) {
          event.preventDefault();
          this.open(trigger);
        }
      });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-search-close]')) this.close();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        trapFocus(this.querySelector('.search-modal__panel'), event);
      });
      if (this.predictive && this.input) {
        this.input.addEventListener('input', debounce(() => this.search(), 260));
      }
    }

    open(trigger) {
      this.trigger = trigger;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      setTimeout(() => this.input && this.input.focus(), 50);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (this.trigger) this.trigger.focus({ preventScroll: true });
    }

    async search() {
      const terms = this.input.value.trim();
      if (!terms) {
        this.results.innerHTML = this.defaultContent;
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: terms,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,page,query',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
      });
      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`, {
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(response.status);
        const doc = parseHTML(await response.text());
        const results = doc.getElementById('predictive-search-results');
        this.results.innerHTML = results ? results.outerHTML : this.defaultContent;
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = this.defaultContent;
      }
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ==========================================================================
     Hero slideshow
     ========================================================================== */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.hero__slide')];
      this.dots = [...this.querySelectorAll('.hero__dot')];
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      if (this.slides.length < 2) return;
      if (!this.delay) this.setAttribute('data-paused', '');

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.slideTo))));

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      let startX = null;
      this.addEventListener('pointerdown', (e) => {
        startX = e.clientX;
      });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const diff = e.clientX - startX;
        if (Math.abs(diff) > 50) this.go(this.index + (diff < 0 ? 1 : -1));
        startX = null;
      });

      this.onBlockSelect = (event) => {
        const i = this.slides.indexOf(event.target);
        if (i >= 0) {
          this.go(i);
          this.pause();
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.play();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    go(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
      this.dots.forEach((dot, i) => {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
        if (i === this.index) {
          void dot.offsetWidth;
          dot.classList.add('is-active');
          dot.setAttribute('aria-current', 'true');
        }
      });
      if (!this.paused) this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.delay) this.timer = setTimeout(() => this.go(this.index + 1), this.delay);
    }

    play() {
      if (!this.delay) return;
      this.paused = false;
      this.removeAttribute('data-paused');
      this.go(this.index);
    }

    pause() {
      this.paused = true;
      this.setAttribute('data-paused', '');
      clearTimeout(this.timer);
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* ==========================================================================
     Generic horizontal slider (testimonials)
     ========================================================================== */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;
      const step = () => {
        const item = this.track.firstElementChild;
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      };
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }

    update() {
      if (!this.prev || !this.next) return;
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ==========================================================================
     Quantity input
     ========================================================================== */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', () => {
          const step = Number(this.input.step) || 1;
          const min = Number(this.input.min) || 0;
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const current = Number(this.input.value) || 0;
          const next = button.name === 'plus' ? current + step : current - step;
          this.input.value = Math.min(max, Math.max(min, next));
          if (String(current) !== this.input.value) {
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ==========================================================================
     Product form (product page + quick add)
     ========================================================================== */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.form.querySelector('[type="submit"][name="add"]') || this.form.querySelector('[type="submit"]');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      const error = this.form.querySelector('[data-form-error]');
      if (error) error.hidden = true;

      const sticky = document.querySelector(`sticky-atc[data-section="${this.dataset.section}"] [data-sticky-add]`);
      [button, sticky].forEach((b) => b && b.classList.add('is-loading'));

      try {
        const data = await addToCart(new FormData(this.form));
        afterAddToCart(data);
      } catch (err) {
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        } else {
          showToast(err.message);
        }
      } finally {
        [button, sticky].forEach((b) => b && b.classList.remove('is-loading'));
      }
    }
  }
  customElements.define('product-form', ProductForm);

  // Quick add buttons inside the cart drawer recommendations
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    button.classList.add('is-loading');
    const formData = new FormData();
    formData.append('id', button.dataset.quickAdd);
    formData.append('quantity', '1');
    try {
      const data = await addToCart(formData);
      renderCartSections(data.sections);
    } catch (err) {
      showToast(err.message);
    } finally {
      button.classList.remove('is-loading');
    }
  });

  /* ==========================================================================
     Cart items (drawer + page)
     ========================================================================== */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.pending = {};
      this.flush = debounce(() => {
        Object.entries(this.pending).forEach(([line, qty]) => this.update(line, qty));
        this.pending = {};
      }, 350);

      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-line]');
        if (!input) return;
        this.pending[input.dataset.line] = input.value;
        this.flush();
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (!remove) return;
        event.preventDefault();
        this.update(remove.dataset.removeLine, 0);
      });
    }

    async update(line, quantity) {
      const item = this.querySelector(`.cart-item[data-line="${line}"]`);
      if (item) item.classList.add('is-loading');
      try {
        const response = await fetch(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity),
            sections: getCartSections(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
        renderCartSections(data.sections);
      } catch (err) {
        if (item) item.classList.remove('is-loading');
        const error = this.querySelector(`[data-line-error="${line}"]`);
        if (error) {
          error.textContent = err.message;
          error.hidden = false;
        } else {
          showToast(err.message);
        }
      }
    }
  }
  customElements.define('cart-items', CartItems);

  // Save cart notes without leaving the page
  document.addEventListener(
    'change',
    debounce((event) => {
      const note = event.target.closest && event.target.closest('[data-cart-note]');
      if (!note) return;
      fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ note: note.value }),
      });
    }, 100)
  );

  /* ==========================================================================
     Product gallery
     ========================================================================== */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      if (!this.main) return;

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex)))
      );

      this.main.addEventListener(
        'scroll',
        debounce(() => {
          if (!this.isScrollable()) return;
          const index = Math.round(this.main.scrollLeft / this.main.clientWidth);
          this.setActive(index);
        }, 60),
        { passive: true }
      );

      this.main.addEventListener('click', (event) => {
        const media = event.target.closest('[data-zoom]');
        if (media) this.openLightbox(media.dataset.zoom, media.querySelector('img')?.alt || '');
      });
    }

    get slides() {
      return [...this.main.children];
    }

    isScrollable() {
      return this.main.scrollWidth > this.main.clientWidth + 4;
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide) return;
      if (this.isScrollable()) {
        this.main.scrollTo({ left: index * this.main.clientWidth, behavior: 'smooth' });
      } else if (index !== 0) {
        this.main.prepend(slide);
        index = 0;
      }
      this.setActive(index);
    }

    setActive(index) {
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      const activeThumb = this.thumbs[index];
      if (activeThumb && activeThumb.parentElement.scrollWidth > activeThumb.parentElement.clientWidth) {
        activeThumb.parentElement.scrollTo({ left: activeThumb.offsetLeft - 40, behavior: 'smooth' });
      }
    }

    setActiveMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.goTo(index);
    }

    openLightbox(src, alt) {
      const box = document.createElement('div');
      box.className = 'lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.innerHTML = `<img src="${src}" alt=""><button type="button" class="lightbox__close" aria-label="Close"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 5 14 14M19 5 5 19" stroke-linecap="round"/></svg></button>`;
      box.querySelector('img').alt = alt;
      const close = () => {
        box.remove();
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keyup', onKey);
      };
      const onKey = (e) => e.key === 'Escape' && close();
      box.addEventListener('click', close);
      document.addEventListener('keyup', onKey);
      document.body.appendChild(box);
      document.body.classList.add('overflow-hidden');
      box.querySelector('button').focus();
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ==========================================================================
     Variant picker
     ========================================================================== */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.section = this.dataset.section;
      this.url = this.dataset.url;
      this.addEventListener('change', () => this.onChange());
    }

    selectedOptions() {
      const values = [];
      this.querySelectorAll('fieldset.option-group, .option-group select').forEach((group) => {
        if (group.tagName === 'SELECT') {
          values[Number(group.dataset.optionIndex)] = group.value;
        } else {
          const checked = group.querySelector('input:checked');
          if (checked) values[Number(checked.dataset.optionIndex)] = checked.value;
          const label = group.querySelector('[data-selected-value]');
          if (label && checked) label.textContent = checked.value;
        }
      });
      return values;
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
      const form = document.getElementById(`ProductForm-${this.section}`);
      const button = form && form.querySelector('[data-add-button]');
      const label = form && form.querySelector('[data-add-label]');

      if (!variant) {
        if (button) button.disabled = true;
        if (label) label.textContent = theme.strings.unavailable;
        return;
      }

      const input = form && form.querySelector('[data-variant-input]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;

      window.history.replaceState({}, '', `${this.url}?variant=${variant.id}`);

      if (variant.featured_media) {
        const gallery = document.querySelector(`product-gallery[data-section="${this.section}"]`);
        if (gallery) gallery.setActiveMedia(variant.featured_media.id);
      }

      this.refresh(variant.id);
    }

    async refresh(variantId) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const response = await fetch(`${this.url}?variant=${variantId}&section_id=${this.section}`, {
          signal: this.controller.signal,
        });
        const doc = parseHTML(await response.text());
        ['Price', 'Inventory', 'StickyPrice'].forEach((prefix) => {
          const next = doc.getElementById(`${prefix}-${this.section}`);
          const current = document.getElementById(`${prefix}-${this.section}`);
          if (next && current) current.innerHTML = next.innerHTML;
        });

        // Sync availability markers without re-rendering inputs (keeps focus)
        const nextPicker = doc.getElementById(`VariantPicker-${this.section}`);
        if (nextPicker) {
          const nextValues = nextPicker.querySelectorAll('.option-value');
          this.querySelectorAll('.option-value').forEach((el, i) => {
            if (nextValues[i]) el.className = nextValues[i].className;
          });
          const nextOptions = nextPicker.querySelectorAll('option');
          this.querySelectorAll('option').forEach((el, i) => {
            if (nextOptions[i]) el.textContent = nextOptions[i].textContent;
          });
        }
      } catch (error) {
        /* aborted or offline: keep the client-side state */
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ==========================================================================
     Sticky add to cart
     ========================================================================== */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(`BuyButtons-${this.dataset.section}`);
      const button = this.querySelector('[data-sticky-add]');
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        if (button) button.tabIndex = show ? 0 : -1;
      });
      this.observer.observe(target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ==========================================================================
     Share button
     ========================================================================== */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('button');
      const label = this.querySelector('[data-share-label]');
      if (!button) return;
      const original = label ? label.textContent : '';
      button.addEventListener('click', async () => {
        const url = button.dataset.shareUrl || window.location.href;
        if (navigator.share) {
          try {
            await navigator.share({ title: button.dataset.shareTitle, url });
          } catch (e) {
            /* dismissed */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          if (label) {
            label.textContent = theme.strings.linkCopied || 'Link copied';
            setTimeout(() => (label.textContent = original), 2000);
          }
        } catch (e) {
          window.prompt('', url);
        }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ==========================================================================
     Collection filters & sorting
     ========================================================================== */
  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facets-form]');
      this.sort = this.querySelector('[data-sort-select]');
      this.drawer = this.querySelector('#FacetDrawer');

      this.addEventListener('change', (event) => {
        if (event.target === this.sort) {
          this.apply();
          return;
        }
        if (!this.form || !this.form.contains(event.target)) return;
        const drawerMode = this.drawer && getComputedStyle(this.drawer).position === 'fixed';
        if (!drawerMode) this.apply();
      });
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply();
        });
      }
    }

    apply() {
      const params = new URLSearchParams();
      if (this.form) {
        for (const [key, value] of new FormData(this.form)) {
          if (value !== '') params.append(key, value);
        }
      }
      if (this.sort && !params.has('sort_by')) params.set('sort_by', this.sort.value);
      const query = params.toString();
      window.location.href = `${this.dataset.url}${query ? `?${query}` : ''}`;
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ==========================================================================
     Product recommendations
     ========================================================================== */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const doc = parseHTML(html);
            const fresh = doc.querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
              this.dataset.loaded = 'true';
              this.classList.add('is-loaded');
              initReveal(this);
            } else {
              this.hidden = true;
            }
          })
          .catch(() => {
            this.hidden = true;
          });
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ==========================================================================
     Misc: dialogs, localization, account panels
     ========================================================================== */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    if (event.target.closest('[data-dialog-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target.tagName === 'DIALOG') event.target.close();

    const toggle = event.target.closest('[data-account-toggle]');
    if (toggle) {
      event.preventDefault();
      showAccountPanel(toggle.dataset.accountToggle);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches && event.target.matches('[data-auto-submit]')) event.target.form?.submit();
  });

  const showAccountPanel = (name) => {
    document.querySelectorAll('[data-account-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.accountPanel !== name;
    });
  };
  if (window.location.hash === '#recover' && document.querySelector('[data-account-panel="recover"]')) {
    showAccountPanel('recover');
  }

  /* ---------- Init ---------- */
  const init = () => initReveal();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => initReveal(event.target));
})();
